import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Chip,
  Stack,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { LocalityHubSeoItem, SeoSnapshotBackup, ScheduledReleaseItem } from '../../types/seo';
import SeoAuditScoreCard from './SeoAuditScoreCard';
import RefineRegenerateModal from './RefineRegenerateModal';
import SafeSyncQueueModal from './SafeSyncQueueModal';
import CreateLocalityHubModal from './CreateLocalityHubModal';
import { calculateSeoScore } from '../../utils/seoAIEngine';
import { stripNumericPrices } from '../../utils/priceSanitizer';

interface LocalityHubsSeoTabProps {
  localityHubs: LocalityHubSeoItem[];
  backups: SeoSnapshotBackup[];
  onUpdateHub: (id: string, updated: Partial<LocalityHubSeoItem>) => void;
  onCreateHub?: (newHub: LocalityHubSeoItem) => void | Promise<void>;
  onImmediateSync: (snapshot: SeoSnapshotBackup) => void;
  onQueueRelease: (queueItem: ScheduledReleaseItem) => void;
  onRollback: (backup: SeoSnapshotBackup) => void;
}

export default function LocalityHubsSeoTab({
  localityHubs,
  backups,
  onUpdateHub,
  onCreateHub,
  onImmediateSync,
  onQueueRelease,
  onRollback,
}: LocalityHubsSeoTabProps) {
  const [selectedHubId, setSelectedHubId] = useState<string>(localityHubs[0]?.id || '');
  const [activeSubTab, setActiveSubTab] = useState<number>(0);
  const [htmlViewMode, setHtmlViewMode] = useState<'preview' | 'code'>('preview');

  // Modals state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [refineModalOpen, setRefineModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const activeHub = localityHubs.find((h) => h.id === selectedHubId) || localityHubs[0];

  if (!activeHub) {
    return <Box>
      <Alert severity="info" sx={{ mb: 2 }}>No locality pages are stored in the website database. Create one only when it has useful, project-relevant local information.</Alert>
      <Button variant="contained" onClick={() => setCreateModalOpen(true)}>Create locality page</Button>
      <CreateLocalityHubModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onCreated={(hub) => onCreateHub?.(hub)} />
    </Box>;
  }

  const scoreData = calculateSeoScore({
    title: activeHub.seoTitle,
    description: activeHub.metaDescription,
    hasFaqs: activeHub.faqs.length >= 2,
    hasSchema: !!activeHub.schemaJson?.['@context'],
    hasRera: activeHub.metaDescription.toLowerCase().includes('rera') || activeHub.richGuideHtml.toLowerCase().includes('gujrera'),
    hasVastu: true,
    hasSuratMention: activeHub.seoTitle.toLowerCase().includes('surat') || activeHub.metaDescription.toLowerCase().includes('surat'),
    contentWordCount: activeHub.richGuideHtml.split(' ').length,
  });

  const handleTitleChange = (val: string) => {
    onUpdateHub(activeHub.id, { seoTitle: stripNumericPrices(val), syncStatus: 'draft' });
  };

  const handleDescChange = (val: string) => {
    onUpdateHub(activeHub.id, { metaDescription: stripNumericPrices(val), syncStatus: 'draft' });
  };

  const handleGuideChange = (val: string) => {
    onUpdateHub(activeHub.id, { richGuideHtml: stripNumericPrices(val), syncStatus: 'draft' });
  };

  const handleAddFaq = () => {
    const newFaqs = [
      ...activeHub.faqs,
      {
        question: '',
        answer: '',
      },
    ];
    onUpdateHub(activeHub.id, { faqs: newFaqs, syncStatus: 'draft' });
  };

  const handleRemoveFaq = (index: number) => {
    const newFaqs = activeHub.faqs.filter((_, idx) => idx !== index);
    onUpdateHub(activeHub.id, { faqs: newFaqs, syncStatus: 'draft' });
  };

  const handleFaqChange = (index: number, field: 'question' | 'answer', val: string) => {
    const newFaqs = activeHub.faqs.map((f, idx) => (idx === index ? { ...f, [field]: val } : f));
    onUpdateHub(activeHub.id, { faqs: newFaqs, syncStatus: 'draft' });
  };

  const handleApplyRefinement = (refined: {
    title: string;
    description: string;
    richContent?: string;
    faqs?: { question: string; answer: string }[];
  }) => {
    onUpdateHub(activeHub.id, {
      seoTitle: refined.title,
      metaDescription: refined.description,
      ...(refined.richContent ? { richGuideHtml: refined.richContent } : {}),
      ...(refined.faqs ? { faqs: refined.faqs } : {}),
      syncStatus: 'draft',
      lastEditedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST',
    });
  };

  const handleCopySchema = () => {
    navigator.clipboard.writeText(JSON.stringify(activeHub.schemaJson, null, 2));
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2500);
  };

  return (
    <Box>
      {/* Header Controls */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 320 }}>
            <InputLabel>Select Locality / Project Hub Landing Page</InputLabel>
            <Select value={activeHub.id} label="Select Locality / Project Hub Landing Page" onChange={(e) => setSelectedHubId(e.target.value)}>
              {localityHubs.map((h) => (
                <MenuItem key={h.id} value={h.id}>
                  {h.title} ({h.type === 'project_hub' ? 'Project Hub' : 'Locality Guide'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<AddLocationAltIcon />}
              onClick={() => setCreateModalOpen(true)}
              sx={{ bgcolor: '#9d6f32', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#7a5525' } }}
            >
              + Create Locality Hub
            </Button>
            <Button
              variant="contained"
              startIcon={<ShieldOutlinedIcon />}
              onClick={() => setSyncModalOpen(true)}
              sx={{ bgcolor: '#2e7d32', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Save page
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {/* Main Content Area */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff', mb: 3 }}>
            {/* Title & Info Banner */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LocationOnIcon sx={{ color: '#9d6f32' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {activeHub.title}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Chip label={`${activeHub.activeListingsCount} Active Units`} size="small" sx={{ fontWeight: 700, bgcolor: '#f4f4f4' }} />
                <Chip label={`Growth: ${activeHub.annualAppreciationRate}`} size="small" color="success" sx={{ fontWeight: 700 }} />
              </Stack>
            </Stack>

            {/* Sub-Tabs */}
            <Tabs
              value={activeSubTab}
              onChange={(_, v) => setActiveSubTab(v)}
              sx={{ borderBottom: '1px solid #e8e8e8', mb: 2.5 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="1. SEO Metadata & SERP" sx={{ fontWeight: 700 }} />
              <Tab label="2. 4-Pillar Connectivity Matrix" sx={{ fontWeight: 700 }} />
              <Tab label="3. 250-Word Living Guide HTML" sx={{ fontWeight: 700 }} />
              <Tab label="4. Locality FAQs" sx={{ fontWeight: 700 }} />
              <Tab label="5. Schema.org Place / FAQPage" sx={{ fontWeight: 700 }} />
            </Tabs>

            {/* Tab 0: Metadata */}
            {activeSubTab === 0 && (
              <Stack spacing={2.5}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                      Locality Page Meta Title (≤ 60 chars)
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: activeHub.seoTitle.length <= 60 ? '#2e7d32' : '#d32f2f' }}>
                      {activeHub.seoTitle.length}/60 chars
                    </Typography>
                  </Stack>
                  <TextField fullWidth size="small" value={activeHub.seoTitle} onChange={(e) => handleTitleChange(e.target.value)} />
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                      Meta Description (Strictly 125 – 155 chars)
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: activeHub.metaDescription.length >= 125 && activeHub.metaDescription.length <= 155 ? '#2e7d32' : '#ed6c02' }}>
                      {activeHub.metaDescription.length} chars
                    </Typography>
                  </Stack>
                  <TextField fullWidth multiline rows={3} value={activeHub.metaDescription} onChange={(e) => handleDescChange(e.target.value)} />
                </Box>

                {/* Google SERP Simulator */}
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fbfbfb', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>
                    Google SERP Simulation for Local Surat Buyers
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: '#202124', display: 'block', fontSize: 11 }}>
                      https://www.swarnagroup.in › localities › {activeHub.slug}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#1a0dab', fontWeight: 500, fontSize: 17, lineHeight: 1.3 }}>
                      {activeHub.seoTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4d5156', fontSize: 13, mt: 0.5, lineHeight: 1.4 }}>
                      {activeHub.metaDescription}
                    </Typography>
                  </Box>
                </Paper>
              </Stack>
            )}

            {/* Tab 1: 4-Pillar Connectivity Matrix */}
            {activeSubTab === 1 && (
              <Stack spacing={2.5}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                  4-Pillar Infrastructure & Neighborhood Matrix (Renders directly on website section 3)
                </Typography>

                <Grid container spacing={2}>
                  {/* Transit */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafaf9' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#9d6f32' }}>
                        🚗 Roads & Transit Highlights
                      </Typography>
                      <Stack spacing={1}>
                        {(activeHub.connectivityHighlights?.transit || [
                          'Direct access via Dandi Road & Palanpur Canal Road corridors',
                          '15–20 mins fast drive to Surat Railway Station & Ring Road',
                          'Direct arterial connectivity toward Adajan, Rander & City Center',
                          'Upcoming Surat Metro Phase connectivity for daily commuters',
                        ]).map((item, idx) => (
                          <TextField
                            key={idx}
                            fullWidth
                            size="small"
                            value={item}
                            onChange={(e) => {
                              const list = [...(activeHub.connectivityHighlights?.transit || [
                                'Direct access via Dandi Road & Palanpur Canal Road corridors',
                                '15–20 mins fast drive to Surat Railway Station & Ring Road',
                                'Direct arterial connectivity toward Adajan, Rander & City Center',
                                'Upcoming Surat Metro Phase connectivity for daily commuters',
                              ])];
                              list[idx] = e.target.value;
                              onUpdateHub(activeHub.id, {
                                connectivityHighlights: {
                                  ...activeHub.connectivityHighlights,
                                  transit: list,
                                },
                                syncStatus: 'draft',
                              });
                            }}
                            sx={{ bgcolor: '#fff' }}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Schools */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafaf9' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#9d6f32' }}>
                        🏫 Top Schools & Education
                      </Typography>
                      <Stack spacing={1}>
                        {(activeHub.connectivityHighlights?.schools || [
                          'Top CBSE & GSEB English medium schools within 5–10 mins',
                          'Preschools, high-school academies & daycare centers nearby',
                          'Safe, wide residential access roads for school bus pickup',
                        ]).map((item, idx) => (
                          <TextField
                            key={idx}
                            fullWidth
                            size="small"
                            value={item}
                            onChange={(e) => {
                              const list = [...(activeHub.connectivityHighlights?.schools || [
                                'Top CBSE & GSEB English medium schools within 5–10 mins',
                                'Preschools, high-school academies & daycare centers nearby',
                                'Safe, wide residential access roads for school bus pickup',
                              ])];
                              list[idx] = e.target.value;
                              onUpdateHub(activeHub.id, {
                                connectivityHighlights: {
                                  ...activeHub.connectivityHighlights,
                                  schools: list,
                                },
                                syncStatus: 'draft',
                              });
                            }}
                            sx={{ bgcolor: '#fff' }}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Hospitals */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafaf9' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#9d6f32' }}>
                        🏥 Healthcare & Hospitals
                      </Typography>
                      <Stack spacing={1}>
                        {(activeHub.connectivityHighlights?.hospitals || [
                          '24/7 Multi-specialty hospitals & emergency care within 3–4 km',
                          'Local pharmacies, diagnostic clinics & pediatric specialists',
                          'Rapid ambulance and arterial road connectivity to major medical hubs',
                        ]).map((item, idx) => (
                          <TextField
                            key={idx}
                            fullWidth
                            size="small"
                            value={item}
                            onChange={(e) => {
                              const list = [...(activeHub.connectivityHighlights?.hospitals || [
                                '24/7 Multi-specialty hospitals & emergency care within 3–4 km',
                                'Local pharmacies, diagnostic clinics & pediatric specialists',
                                'Rapid ambulance and arterial road connectivity to major medical hubs',
                              ])];
                              list[idx] = e.target.value;
                              onUpdateHub(activeHub.id, {
                                connectivityHighlights: {
                                  ...activeHub.connectivityHighlights,
                                  hospitals: list,
                                },
                                syncStatus: 'draft',
                              });
                            }}
                            sx={{ bgcolor: '#fff' }}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Lifestyle */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafaf9' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#9d6f32' }}>
                        🛍️ Retail & Daily Conveniences
                      </Typography>
                      <Stack spacing={1}>
                        {(activeHub.connectivityHighlights?.lifestyle || [
                          'Daily fresh vegetable markets, D-Mart & supermarkets within 5 mins',
                          'Family dining, cafes, fitness centers & municipal public gardens',
                          'Peaceful residential neighborhood surrounded by open green belts',
                        ]).map((item, idx) => (
                          <TextField
                            key={idx}
                            fullWidth
                            size="small"
                            value={item}
                            onChange={(e) => {
                              const list = [...(activeHub.connectivityHighlights?.lifestyle || [
                                'Daily fresh vegetable markets, D-Mart & supermarkets within 5 mins',
                                'Family dining, cafes, fitness centers & municipal public gardens',
                                'Peaceful residential neighborhood surrounded by open green belts',
                              ])];
                              list[idx] = e.target.value;
                              onUpdateHub(activeHub.id, {
                                connectivityHighlights: {
                                  ...activeHub.connectivityHighlights,
                                  lifestyle: list,
                                },
                                syncStatus: 'draft',
                              });
                            }}
                            sx={{ bgcolor: '#fff' }}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Stack>
            )}

            {/* Tab 2: 250-Word Guide */}
            {activeSubTab === 2 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                    250-Word Local Area, Metro & Infrastructure Guide (HTML)
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant={htmlViewMode === 'preview' ? 'contained' : 'outlined'}
                      startIcon={<VisibilityIcon />}
                      onClick={() => setHtmlViewMode('preview')}
                      sx={{ fontSize: 11, height: 28 }}
                    >
                      Visual Preview
                    </Button>
                    <Button
                      size="small"
                      variant={htmlViewMode === 'code' ? 'contained' : 'outlined'}
                      startIcon={<CodeIcon />}
                      onClick={() => setHtmlViewMode('code')}
                      sx={{ fontSize: 11, height: 28 }}
                    >
                      HTML Code
                    </Button>
                  </Stack>
                </Stack>

                {htmlViewMode === 'preview' ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: '#fff',
                      lineHeight: 1.6,
                      fontSize: 14,
                      color: '#333',
                      '& h3': { color: '#9d6f32', fontSize: 17, mt: 0, mb: 1.5 },
                      '& p': { mb: 1.5 },
                    }}
                    dangerouslySetInnerHTML={{ __html: activeHub.richGuideHtml }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    value={activeHub.richGuideHtml}
                    onChange={(e) => handleGuideChange(e.target.value)}
                    sx={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                )}
              </Box>
            )}

            {/* Tab 3: FAQs */}
            {activeSubTab === 3 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Locality & Buyer FAQs ({activeHub.faqs.length})
                  </Typography>
                  <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleAddFaq} sx={{ color: '#9d6f32', fontWeight: 700 }}>
                    + Add FAQ
                  </Button>
                </Stack>

                <Stack spacing={2}>
                  {activeHub.faqs.map((faq, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafaf9' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                        <TextField
                          label={`Question #${idx + 1}`}
                          fullWidth
                          size="small"
                          value={faq.question}
                          onChange={(e) => handleFaqChange(idx, 'question', e.target.value)}
                          sx={{ mr: 1, bgcolor: '#fff' }}
                        />
                        <IconButton size="small" color="error" onClick={() => handleRemoveFaq(idx)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <TextField
                        label="Answer"
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        value={faq.answer}
                        onChange={(e) => handleFaqChange(idx, 'answer', e.target.value)}
                        sx={{ bgcolor: '#fff' }}
                      />
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Tab 4: Schema */}
            {activeSubTab === 4 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                    Schema.org Place / ApartmentComplex JSON-LD
                  </Typography>
                  <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopySchema} sx={{ color: '#9d6f32', fontWeight: 700 }}>
                    {copySuccess ? 'Copied JSON-LD!' : 'Copy Structured Data'}
                  </Button>
                </Stack>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#1e1e1e', color: '#4ec9b0', fontFamily: 'monospace', fontSize: 12, borderRadius: 2, maxHeight: 320, overflow: 'auto' }}>
                  <pre style={{ margin: 0 }}>{JSON.stringify(activeHub.schemaJson, null, 2)}</pre>
                </Paper>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Column: Score & Details */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            <SeoAuditScoreCard score={scoreData.score} checks={scoreData.checks} />

            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Locality Intelligence Stats
              </Typography>
              <Stack spacing={1.5} sx={{ fontSize: 13 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Micro-Market:</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{activeHub.microMarket}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Hub Type:</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{activeHub.type.toUpperCase()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Active Listings:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#2e7d32' }}>{activeHub.activeListingsCount} Units</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography color="text.secondary">Live URL:</Typography>
                  <Button
                    size="small"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                    href={`https://www.swarnagroup.in/localities/${activeHub.slug}`}
                    target="_blank"
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: 11, p: 0, color: '#9d6f32' }}
                  >
                    /localities/{activeHub.slug}
                  </Button>
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Create Locality Hub Modal */}
      <CreateLocalityHubModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={async (newHub) => {
          await onCreateHub?.(newHub);
          setSelectedHubId(newHub.id);
        }}
      />

      {/* Safe Sync & Queue Modal */}
      <SafeSyncQueueModal
        open={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        entityId={activeHub.id}
        entityType="LOCALITY"
        entityName={activeHub.title}
        currentData={{
          seoTitle: activeHub.seoTitle,
          metaDescription: activeHub.metaDescription,
          richContentHtml: activeHub.richGuideHtml,
          faqs: activeHub.faqs,
          schemaJson: activeHub.schemaJson,
        }}
        backups={backups}
        onImmediateSync={(snapshot) => {
          onImmediateSync(snapshot);
          onUpdateHub(activeHub.id, { syncStatus: 'synced', lastSyncedAt: snapshot.timestamp });
        }}
        onQueueRelease={(queueItem) => {
          onQueueRelease(queueItem);
          onUpdateHub(activeHub.id, { syncStatus: 'staged' });
        }}
        onRollback={(backup) => {
          onRollback(backup);
          onUpdateHub(activeHub.id, {
            seoTitle: backup.snapshotData.seoTitle,
            metaDescription: backup.snapshotData.metaDescription,
            richGuideHtml: backup.snapshotData.richContentHtml,
            faqs: backup.snapshotData.faqs,
            schemaJson: backup.snapshotData.schemaJson,
            syncStatus: 'synced',
          });
        }}
      />
    </Box>
  );
}
